/**
 * "Compare two runs" (`/runs/compare?a=<run>&b=<run>`): two finished runs of one program (the same bank and scenario) side by side: the score at each
 * year-end, the final score in points and dollars a share, and the rank. Every figure is read from the run's own finale (`soloDebrief`), the series the
 * finale draws; nothing is recomputed here.
 */
import { useRoute } from "../api/context";
import { formatPointsWord, formatUsdPerShare } from "../format";
import { rankWords } from "../score/labels";
import { Link, useLocation } from "../shell/history";
import { SoloFrame } from "../solo/SoloFrame";
import { GxCard, GxErrorBanner, GxSkeletonBlock } from "../theme/primitives";
import { comparable, runScore, type RunScore } from "./compare";
import "./runs.css";

function useRunScore(runId: string | null): { readonly name: string | null; readonly score: RunScore | null | undefined } {
  const run = useRoute("soloResume", runId === null ? null : { runId });
  const debrief = useRoute("soloDebrief", runId === null || run.data?.completed !== true ? null : { runId });
  const market = useRoute("publicMarket", runId === null ? null : { cohortId: runId });
  const welcome = useRoute("welcomeCard", runId === null ? null : { cohortId: runId }, { staleTime: Infinity });
  const tied = market.data?.banks.find((b) => b.teamId === run.data?.teamId)?.score?.tiedWithTeamIds ?? [];
  const score = debrief.data === undefined || run.data === undefined || market.data === undefined ? undefined : runScore(debrief.data, run.data.teamId, tied as string[]);
  return { name: welcome.data?.cohortName ?? null, score };
}

const cell = (score: RunScore | null | undefined, f: (s: RunScore) => string): string => (score === undefined ? "…" : score === null ? "–" : f(score));

export function ComparePage() {
  const { search } = useLocation();
  const q = new URLSearchParams(search);
  const a = q.get("a");
  const b = q.get("b");
  const list = useRoute("soloList", undefined);
  const ok = list.data !== undefined && a !== null && b !== null && comparable(list.data.runs, a, b);
  const A = useRunScore(ok ? a : null);
  const B = useRunScore(ok ? b : null);
  const finished = (list.data?.runs ?? []).filter((r) => r.phase === "completed");
  const years = Math.max(A.score?.years.length ?? 0, B.score?.years.length ?? 0);
  return (
    <SoloFrame bankName="Governexus" subline="Compare runs" title="Compare runs">
      <div className="gx-runs" data-testid="compare">
        <div className="gx-pagehead"><div><div className="gx-eyebrow">Your runs</div><h1 className="gx-h1">Compare two runs</h1></div><Link href="/runs" className="gx-btn gx-btn--secondary">Your runs</Link></div>
        {list.error !== null ? <GxErrorBanner error={list.error.result.error} /> : null}
        {list.isLoading ? <GxSkeletonBlock height={160} lines={4} /> : null}
        {list.data !== undefined && !ok ? (
          <GxCard title="Pick two finished runs of one program" testId="compare.pick">
            <div className="gx-card-b">
              {a !== null || b !== null ? <p className="gx-muted" data-testid="compare.refused">Those two runs cannot be compared: both must be finished runs of the same program, which gives them the same bank and scenario.</p> : null}
              {finished.length < 2 ? <p className="gx-muted">Finish two runs of the same program to compare them.</p> : (
                <ul className="gx-rl-list">{finished.map((r, i) => finished.slice(i + 1).filter((o) => o.programId === r.programId).map((o) => (
                  <li key={`${r.runId}.${o.runId}`} className="gx-rl-row"><span><RunLabel runId={r.runId} /> and <RunLabel runId={o.runId} /></span><Link className="gx-btn gx-btn--primary" href={`/runs/compare?a=${r.runId}&b=${o.runId}`}>Compare</Link></li>
                )))}</ul>
              )}
            </div>
          </GxCard>
        ) : null}
        {ok ? (
          <GxCard title="Side by side" testId="compare.table">
            <div className="gx-card-b">
              <table className="gx-cmp gx-num">
                <thead><tr><th scope="col"><span className="gx-sr">Measure</span></th><th scope="col" data-testid="compare.a">{A.name ?? "Run A"}</th><th scope="col" data-testid="compare.b">{B.name ?? "Run B"}</th></tr></thead>
                <tbody>
                  {Array.from({ length: years }, (_, i) => {
                    const y = (A.score ?? B.score)?.years[i];
                    return <tr key={i} data-testid={`compare.year.${i + 1}`}><th scope="row">{y?.kind === "lookThrough" ? `End of year ${i + 1} (look-through)` : `End of year ${i + 1}`}</th><td>{cell(A.score, (s) => formatPointsWord(s.years[i]?.rate ?? 0, { signed: true, dp: 2 }))}</td><td>{cell(B.score, (s) => formatPointsWord(s.years[i]?.rate ?? 0, { signed: true, dp: 2 }))}</td></tr>;
                  })}
                  <tr data-testid="compare.final"><th scope="row">Final score vs the twin</th><td>{cell(A.score, (s) => `${formatPointsWord(s.finalRate, { signed: true, dp: 2 })} · ${formatUsdPerShare(s.finalUsdPerShare, { signed: true })} a share`)}</td><td>{cell(B.score, (s) => `${formatPointsWord(s.finalRate, { signed: true, dp: 2 })} · ${formatUsdPerShare(s.finalUsdPerShare, { signed: true })} a share`)}</td></tr>
                  <tr data-testid="compare.rank"><th scope="row">Rank among that run&apos;s six banks</th><td>{cell(A.score, (s) => (s.rank === null ? "–" : rankWords(s.rank.rank, s.rank.of, s.rank.tiedWith.length > 0)))}</td><td>{cell(B.score, (s) => (s.rank === null ? "–" : rankWords(s.rank.rank, s.rank.of, s.rank.tiedWith.length > 0)))}</td></tr>
                </tbody>
              </table>
              <p className="gx-small gx-faint" data-testid="compare.note">Same bank and scenario. Each run draws its own market path, so every score is measured against that run&apos;s own twin, and the rank is among that run&apos;s five AI banks. Scores are the running total at each year-end, so the last row is the final score.</p>
            </div>
          </GxCard>
        ) : null}
      </div>
    </SoloFrame>
  );
}

function RunLabel({ runId }: { readonly runId: string }) {
  const w = useRoute("welcomeCard", { cohortId: runId }, { staleTime: Infinity });
  return <b>{w.data?.cohortName ?? "Your run"}</b>;
}
